import { DashboardItemData } from 'framework/ui/dashboard';
import { AssignmentFormData, AssignmentType } from 'domain/shared/assignment/types';
import { ExpectedStructureType, ProjectTeamType } from 'domain/shared/newProject';
import { UpcomingChangesType } from '../upcomingChanges';
import { STAFFING_PRIORIRY_ZONES_ALIASES, STAFFING_RISK_STATUS } from './constants';

export type HealthZone = keyof typeof STAFFING_PRIORIRY_ZONES_ALIASES;

export type STAFFING_RISK_STATUS_TYPE = keyof typeof STAFFING_RISK_STATUS;

export const STAFFING_RISK_PRIORITY = {
  SAFE: 'SAFE',
  MONITORING: 'MONITORING',
  RISK: 'RISK',
} as const;

interface StaffingCriticalPeriodType {
  id: string;
  projectId?: string;
  title: string;
  description?: string;
  startDate: string;
  endDate: string;
}

interface EngagementType {
  id: string;
  crmId: string;
  name: string;
  accountId: string;
  expectedStartDate: string;
  status: string;
  relatedProjectId: string;
  rawProjectId: string;
  deletedAt?: string;
}

export type StaffingAssignment = {
  id: string;
  ownerAlias?: string;
  employeeAlias: string;
  positionId?: StringOrNumber | null;
  technologyId?: StringOrNumber | null;
  isVirtual?: boolean;
  isBackup?: boolean;
  isSpof?: boolean;
  isBench?: boolean;
  fromDate?: string;
  toDate?: string;
  minPercent?: number;
  maxPercent?: number;
  slotId?: string | null;
  membershipRole?: string | null;
  competencyLevel?: string | null;
  isProbation?: boolean;
  status?: string;
  noteText?: string | null;
  updatedAt?: string;
};

// shared
export interface StaffingDataType extends DashboardItemData {
  id: string | number;
  name: string;
  alias: string;
  projectManager: string;
  staffingCoordinator?: string;
  description?: string;
  budget?: {
    id?: string;
    startDate?: string;
    endDate?: string;
    softStartDate?: string;
    billingType?: string;
    fte?: string;
  };
  isArchived?: boolean;
  accountData?: {
    accountName?: string;
    accountId?: string;
    ownerAlias?: string;
  };
  team?: ProjectTeamType;
  staffingRisk?: StaffingRisk;
  staffingNote?: StaffingNoteItem[];
  criticalPeriods?: StaffingCriticalPeriodType[];
  upcomingChanges?: UpcomingChangesType[];
  assignments?: StaffingAssignment[];
  engagement?: EngagementType;
  teamChart: {
    fteRate: FteRateChart[];
    tenureDistribution: TenureDistributionChart[];
    averageTenure: AverageTenureChartType[];
  };
}

export type StaffingProjectData = Pick<StaffingDataType, 'id' | 'budget' | 'assignments'> & {
  name?: string;
  projectManager?: string;
  team?: Partial<ProjectTeamType>;
  legacyId?: number;
};

export type SetStaffingProjectData = (data: StaffingProjectData & { team: Partial<ProjectTeamType> }) => void;

export type StaffingAssignmentListMember = {
  id: string;
  assignmentId: string;
  employeeId: string;
  employeeAlias: string;
  slotId?: string | null;
  positionId?: Nullable<StringOrNumber>;
  technologyId?: Nullable<string>;
  competencyLevel?: Nullable<string>;
  fte: number;
  role?: string | null;
  isProbation: boolean;
  isBackup: boolean;
  isSpof: boolean;
  isBench: boolean;
  startDate?: string;
  endDate?: string;
  noteText?: string | null;
  issues: string[];
  membershipRole?: string | null;
  minPercent: number;
  maxPercent: number;
  backup: boolean;
  bench: boolean;
  from?: string;
  to?: string;
  isVirtual?: boolean;
  status: string;
  isPending: boolean;
};

export type StaffingAssignmentSlot = ExpectedStructureType & {
  issues: string[];
  assignedFte: string | number;
  members: StaffingAssignmentListMember[];
  notes?: string;
};

export type AssignmentModalInitialData = AssignmentFormData & {
  id: string;
  assignmentType: AssignmentType;
  employeeId: string;
  target?: {
    id?: number;
    type: 'PROJECT';
    displayName: string;
  };
};

export type StaffingProjectAssignment = StaffingAssignment & {
  projectId?: string | number;
  projectName?: string;
  status?: string;
};

export interface StaffingRisk {
  id: string;
  projectId: string;
  priority: HealthZone;
  issues: StaffingRiskIssue[];
  followUps: StaffingRiskFollowUp[];
  createdAt: string;
  updatedAt?: string;
}

export interface StaffingRiskIssue {
  id: string;
  description: string;
  status: STAFFING_RISK_STATUS_TYPE;
  targetPriority: HealthZone;
  systemIssueType?: string;
  closedDate?: string;
  staffingRiskId: string;
  createdAt: string;
  updatedAt?: string;
  isNew?: boolean;
}

interface StaffingRiskFollowUp {
  id?: string;
  description: string;
  staffingRiskIssueId?: string;
  createdAt?: string;
  updatedAt?: string;
  followUpDate?: string;
  status?: any;
}

export interface StaffingRiskByPriority {
  SAFE: StaffingDataType[];
  MONITORING: StaffingDataType[];
  CRITICAL: StaffingDataType[];
}

export interface StaffingNoteItem {
  id: string;
  dateTime?: string;
  author: string | null;
  noteText: string;
  attachmentPaths: string[];
  projectId?: string;
}

export type FteRateChart = {
  id: number;
  projectId: string;
  projectName: string;
  startDate: string;
  avgRequiredFte: number;
  staffingRate: number;
  avgActualFte: number;
};
export type TenureDistributionChart = {
  projectName: string;
  durationRange: string;
  employeeCount: number;
  employeePercent: number;
};
export type AverageTenureChartType = {
  id: number;
  projectId: string;
  projectName: string;
  endMonth: string;
  averageTenure: number;
};
